"use client";

import { useEffect, useId, useRef, useSyncExternalStore, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";
import { IconButton } from "./button";
import { Icon, X } from "./icon";

export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * "viewport" (default): fixed overlay on z.modal above z.scrim. Rendered
   * through a portal to <body> on the client so a transformed / filtered
   * ancestor (e.g. a card with a hover `transform`) can't become the containing
   * block for the fixed scrim + dialog — which would trap the modal inside that
   * ancestor's box, mis-centre it, and let clicks fall through to the content
   * behind it. SSR still emits nothing (real usages are `open=false` on the
   * server), so the portal is a client-only escape hatch and stays deterministic.
   * "inline": contained inside a `.pz-well` ancestor (gallery previews, embed
   * contexts) — never portalled, so it server-renders in place deterministically.
   */
  layout?: "viewport" | "inline";
  className?: string;
}

const emptySubscribe = () => () => {};

/** Elements a keyboard user can land on inside the dialog (MAP-117 focus trap). */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusable(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * Pure focus-trap math (MAP-117), split out so it's unit-testable without a
 * real DOM (this project's tests run in the `node` vitest environment — no
 * jsdom/testing-library — see site-nav-auth.test.tsx). Given how many
 * focusable elements the dialog has, which one (by index, -1 = none/unknown)
 * currently has focus, and whether Shift is held, returns the index Tab
 * should land on to keep focus trapped (wrapping at both ends), or `null`
 * when the browser's native Tab behavior already stays inside the dialog and
 * nothing needs to be intercepted.
 */
export function trapFocusIndex(count: number, activeIndex: number, shiftKey: boolean): number | null {
  if (count === 0) return null;
  if (shiftKey) return activeIndex <= 0 ? count - 1 : null;
  return activeIndex === -1 || activeIndex === count - 1 ? 0 : null;
}

/**
 * Registry of currently-open Modal instances, most-recently-opened last
 * (verified live against a real double-mount: two open Modals each
 * installing a plain `document` keydown listener fight over Tab/Escape with
 * no ordering rule — pressing Tab in the top one got hijacked by the other,
 * which saw an unrecognized `document.activeElement` and yanked focus back
 * to itself). Every Modal's keydown handler checks it is the TOPMOST open
 * instance before acting, so Escape/Tab only ever affect the modal actually
 * on top when more than one happens to be open (e.g. a confirm dialog opened
 * from within another modal) — the standard modal-stacking rule.
 */
let openModalStack: symbol[] = [];

export function Modal({ open, onClose, title, children, footer, layout = "viewport", className }: ModalProps) {
  // Portalling needs a DOM target (document.body), so it can only happen on the
  // client. useSyncExternalStore gives `false` during SSR + the first hydration
  // render and `true` thereafter — the canonical "is mounted" read, with no
  // setState-in-effect (which the render-safety lint forbids).
  const mounted = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Latest onClose in a ref so the keydown listener (attached once per
  // open/close transition, see below) never has to re-subscribe just because
  // a parent re-render handed it a new inline arrow function — re-subscribing
  // on every render would re-run the "capture + steal initial focus" setup
  // too, fighting the user's own focus changes inside an already-open modal.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  // Stable per-instance identity for the open-modal stack (see above) — a
  // fresh Symbol is a trivial, immediately-collectable allocation on renders
  // after the first; only the one from the initial render is ever kept.
  const tokenRef = useRef(Symbol("pz-modal"));

  // Escape-to-close + focus trap + focus-return (MAP-117). Runs once per
  // open/close transition (deps: [open] only — see onCloseRef above).
  useEffect(() => {
    if (!open) return;
    const token = tokenRef.current;
    openModalStack.push(token);
    const dialog = dialogRef.current;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const initial = getFocusable(dialog)[0];
    (initial ?? dialog)?.focus();

    function onKeyDown(e: KeyboardEvent) {
      // Only the topmost open modal reacts to Escape/Tab — if another Modal
      // opened on top of this one, let it handle the keystroke; this
      // instance's listener stays attached underneath, inert, until it's
      // topmost again.
      if (openModalStack[openModalStack.length - 1] !== token) return;
      if (e.key === "Escape") {
        onCloseRef.current?.();
        return;
      }
      if (e.key !== "Tab") return;
      const items = getFocusable(dialog);
      if (items.length === 0) {
        e.preventDefault();
        dialog?.focus();
        return;
      }
      const activeIndex = items.indexOf(document.activeElement as HTMLElement);
      const next = trapFocusIndex(items.length, activeIndex, e.shiftKey);
      if (next !== null) {
        e.preventDefault();
        items[next]?.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      openModalStack = openModalStack.filter((t) => t !== token);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  const inline = layout === "inline";
  const content = (
    <>
      <div className={cx("pz-scrim", inline && "pz-scrim--inline")} onClick={onClose} aria-hidden />
      <div className={cx("pz-modal-root", inline && "pz-modal-root--inline")}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className={cx("pz-modal", className)}
        >
          <div className="pz-modal__header">
            <h2 className="pz-modal__title" id={titleId}>
              {title}
            </h2>
            {onClose && (
              <IconButton label="Close" variant="ghost" size="sm" onClick={onClose}>
                <Icon icon={X} size="md" />
              </IconButton>
            )}
          </div>
          <div className="pz-modal__body">{children}</div>
          {footer != null && <div className="pz-modal__footer">{footer}</div>}
        </div>
      </div>
    </>
  );

  // Inline modals render in place; viewport modals escape to <body> once mounted
  // (before mount they render in place too, matching any SSR-open markup so
  // hydration stays consistent — real viewport usages are closed at SSR anyway).
  if (inline || !mounted) return content;
  return createPortal(content, document.body);
}
